<template>
  <div>
    <b-modal
      id="modal-hold"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      no-close-on-backdrop
      title="Hold Stock"
    >
      <b-form>
        <b-form-group
          label="Hold till"
          invalid-feedback="Date is required"
          ref="holdDate"
        >
          <flat-pickr
            :config="config"
            v-model="holdObj.holdTill"
            class="form-control"
            :placeholder="$t('Select Date')"
            @on-change="CheckDate()"
          />
        </b-form-group>
      </b-form>
      <b-row class="mt-2">
        <b-col class="d-flex justify-content-end">
          <b-button
            @click="markHold()"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="primary"
            :disabled="holding"
          >
            <b-spinner v-if="holding" small type="grow" />
            <span v-else class="align-middle">Hold</span>
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Bundle Modal -->
    <b-modal
      id="modal-bundle"
      title="Create Bundle"
      size="lg"
      hide-footer
      centered
      no-close-on-backdrop
    >
      <b-form>
        <!-- Selected Product Details -->
        <b-row class="mb-2" v-if="selectedProduct">
          <b-col cols="4">
            <b-img
              :src="
                getInvImagePreviewUrl(selectedProduct.image) ||
                'https://placehold.co/80x80?text=Image'
              "
              height="80px"
              width="80px"
              style="border-radius: 10px"
              alt="product"
              referrerpolicy="no-referrer"
              @error="onInvTableImageError($event, selectedProduct.image)"
            />
          </b-col>
          <b-col cols="8">
            <h6 class="mb-50">{{ selectedProduct.name }}</h6>
            <p class="mb-50">SKU: {{ selectedProduct.sku }}</p>
            <p v-if="!hideSellingPrice" class="mb-0">
              Price: {{ selectedProduct.sellingPrice }}
            </p>
          </b-col>
        </b-row>

        <!-- Bundle Items -->
        <div v-for="(item, index) in bundleItems" :key="index" class="">
          <b-row>
            <b-col md="6">
              <b-form-group
                :label="'Product ' + (index + 1)"
                :invalid-feedback="'Please select a product'"
              >
                <v-select
                  v-model="item.stockID"
                  :options="availableProducts"
                  :reduce="(val) => val.id"
                  :selectable="(opt) => !isSelectedItem(opt)"
                  label="name"
                  :clearable="false"
                  placeholder="Select product"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                label="Quantity"
                :invalid-feedback="'Enter valid quantity'"
              >
                <b-form-input
                  v-model="item.qty"
                  type="number"
                  min="1"
                  placeholder="Enter quantity"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-button
                v-if="index == bundleItems.length - 1"
                variant="primary"
                class="btn-icon mt-2 mr-50"
                pill
                @click="addBundleItem()"
              >
                <feather-icon icon="PlusIcon" />
              </b-button>
              <b-button
                v-if="bundleItems.length > 1"
                variant="outline-danger"
                class="btn-icon mt-2"
                pill
                @click="removeBundleItem(index)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Custom Footer Buttons -->
        <b-row class="mt-2">
          <b-col class="text-right">
            <b-button
              variant="primary"
              @click="saveBundle()"
              :disabled="bundleSaving"
            >
              <b-spinner v-if="bundleSaving" small type="grow" />
              <span v-else>Save Bundle</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>

    <b-sidebar
      id="sidebar-inv"
      bg-variant="white"
      v-model="visibility1"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Inventory</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility1 = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row class="mt-1">
          <b-col md="12">
            <b-form-group
              label=""
              invalid-feedback="Image is required"
              required
              ref="image"
            >
              <div class="inv-photo-upload">
                <div
                  v-if="!invImagePreview && !docLoading"
                  class="inv-photo-upload__slot"
                >
                  <b-img
                    ref="previewEl"
                    src="https://placehold.co/120x120?text=Image"
                    @click="$refs.invImage.click()"
                    style="border-radius: 10px"
                    height="120px"
                    width="120px"
                  />
                </div>

                <div
                  v-else-if="!invImagePreview && docLoading"
                  class="inv-photo-upload__slot"
                >
                  <b-spinner
                    type="grow"
                    label="Loading..."
                    variant="primary"
                  />
                </div>

                <div v-else class="inv-photo-upload__preview">
                  <img
                    :src="invImagePreview"
                    @click="!docLoading && $refs.invImage.click()"
                    @error="onInvImagePreviewError"
                    referrerpolicy="no-referrer"
                    class="inv-photo-upload__img"
                    alt="Product image"
                  />
                  <div v-if="docLoading" class="inv-photo-upload__overlay">
                    <b-spinner variant="primary" type="grow" />
                  </div>
                  <feather-icon
                    v-if="!docLoading"
                    icon="Trash2Icon"
                    size="22"
                    class="text-danger cursor-pointer inv-photo-upload__delete"
                    @click="removeImage()"
                  />
                </div>
                <input
                  type="file"
                  id="invImage"
                  hidden
                  ref="invImage"
                  @change="addImage()"
                  accept="image/*,"
                />
              </div>
            </b-form-group>
          </b-col>
          <!-- Single category: shown only when inventory_settings is OFF -->
          <b-col md="12" v-if="!invSetting || !invSetting.valueBool">
            <b-form-group invalid-feedback="Category is required." ref="catID">
              <div class="d-flex justify-content-between">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >Category</label
                >
                <feather-icon
                  class="cursor-pointer"
                  icon="EditIcon"
                  size="18"
                  v-b-tooltip.hover.top
                  title="Manage Category"
                  @click="openCat(0)"
                />
              </div>
              <v-select
                ref="catID"
                v-model="invObj.categoryID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="categorySelectOptions"
                :reduce="(val) => val.id"
                label="displayName"
                :clearable="false"
                placeholder="select"
              />
            </b-form-group>
          </b-col>

          <!-- Multi category: shown only when inventory_settings is ON (replaces single select) -->
          <b-col md="12" v-if="invSetting && invSetting.valueBool">
            <b-form-group ref="multiCatID">
              <div class="d-flex justify-content-between">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >Category</label
                >
                <feather-icon
                  class="cursor-pointer"
                  icon="EditIcon"
                  size="18"
                  v-b-tooltip.hover.top
                  title="Manage Category"
                  @click="openCat(0)"
                />
              </div>
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="selectedCategories"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="categorySelectOptions"
                :reduce="(val) => val.id"
                label="displayName"
                :clearable="false"
                placeholder="select"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group invalid-feedback="Vendor is required." ref="suppID">
              <div class="d-flex justify-content-between">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >Vendor</label
                >
                <feather-icon
                  class="cursor-pointer"
                  icon="EditIcon"
                  size="18"
                  v-b-tooltip.hover.top
                  title="Manage Vendor"
                  @click="openSupp()"
                />
              </div>
              <v-select
                ref="suppID"
                v-model="invObj.supplierID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="suppliers"
                :reduce="(val) => val.id"
                label="name"
                :clearable="false"
                placeholder="select"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              invalid-feedback="Warehouse is required."
              ref="wareID"
            >
              <div class="d-flex justify-content-between">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >Warehouse</label
                >
                <feather-icon
                  class="cursor-pointer"
                  icon="EditIcon"
                  size="18"
                  v-b-tooltip.hover.top
                  title="Manage Warehouse"
                  @click="openWare()"
                />
              </div>
              <v-select
                ref="wareID"
                v-model="invObj.warehouseID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="warehouses"
                :reduce="(val) => val.id"
                label="name"
                :clearable="false"
                placeholder="select"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group
              label="Classes"
              invalid-feedback="Classes are required."
              ref="classes"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="selectedClasses"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classesOptions"
                :reduce="(val) => val.id"
                label="name"
                :clearable="false"
                placeholder="Select classes"
              />
            </b-form-group>
          </b-col>

          <b-col md="12" v-if="invObj.id > 0 && accObj && accObj.valueBool">
            <b-form-group
              label="Account"
              invalid-feedback="Account is required."
              ref="account"
            >
              <v-select
                v-model="invObj.accountID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="accList"
                :reduce="(opt) => opt.id"
                label="title"
                :clearable="false"
                :disabled="accLoading"
                placeholder="Select account"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group
              label="Product Name"
              invalid-feedback="Product name is required."
            >
              <b-form-input
                ref="pname"
                v-model="invObj.name"
                placeholder="Enter product name here"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="SKU Number"
              invalid-feedback="SKU Number is required."
            >
              <b-form-input
                ref="sku"
                v-model="invObj.sku"
                placeholder="Enter sku"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!hideSellingPrice">
            <b-form-group
              label="Purchase Price"
              invalid-feedback="Purchase Price is required."
            >
              <b-form-input
                ref="pprice"
                type="number"
                v-model="invObj.purchasePrice"
                placeholder="Enter price"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!hideSellingPrice">
            <b-form-group
              label="Selling Price"
              invalid-feedback="Selling Price is required."
            >
              <b-form-input
                ref="sprice"
                type="number"
                v-model="invObj.sellingPrice"
                placeholder="Enter price"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Quantity"
              invalid-feedback="Quantity is required."
            >
              <b-form-input
                ref="quantity"
                type="number"
                v-model="invObj.quantity"
                :disabled="invObj.id > 0"
                placeholder="Enter quantity"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Restock Quantity"
              invalid-feedback="Restock Quantity is required."
            >
              <b-form-input
                ref="restock"
                type="number"
                v-model="invObj.restockQuantity"
                placeholder="Enter restock quantity"
              >
              </b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="12" v-if="invSetting && invSetting.valueBool">
            <b-form-group
              label="Total Value"
            >
              <b-form-input
                type="number"
                v-model="invObj.totalValue"
                placeholder="Total Value"
              >
              </b-form-input>
            </b-form-group>
          </b-col>

          <template v-if="isInventoryIuss">
            <b-col md="12">
              <b-form-group label="Brand">
                <b-form-input
                  v-model="invObj.brand"
                  placeholder="Enter brand"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Warranty">
                <b-form-input
                  v-model="invObj.warranty"
                  placeholder="Enter warranty"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Purchase Date">
                <flat-pickr
                  :config="config"
                  v-model="invObj.purchaseDate"
                  class="form-control"
                  placeholder="Select purchase date"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Specification">
                <b-form-textarea
                  v-model="invObj.specification"
                  rows="3"
                  placeholder="Enter specification"
                />
              </b-form-group>
            </b-col>
          </template>

          <b-col md="12" class="mt-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="success"
              @click="save()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{$t('Save')}}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-Department"
      bg-variant="white"
      v-model="visibility"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Manage Categories</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility = false"
        />
      </div>
      <b-form class="p-2" @submit.prevent>
        <b-row v-if="isInventoryIuss && rights.add" class="mb-1">
          <b-col md="12">
            <div
              class="d-flex flex-wrap align-items-center mb-50"
              style="gap: 0.5rem"
            >
              <b-button
                variant="primary"
                size="sm"
                :disabled="iussImportLoading"
                @click="$refs.iussImportFile.click()"
              >
                <b-spinner v-if="iussImportLoading" small type="grow" />
                <feather-icon v-else icon="UploadIcon" class="mr-50" />
                Import
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="downloadIussCategorySample"
              >
                <feather-icon icon="DownloadIcon" class="mr-50" />
                Sample
              </b-button>
            </div>
            <p class="text-muted small mb-1">
              Import categories &amp; sub categories via
              ImportSubCategories. Columns: brCode, categoryCode, categoryName,
              subCategoryName, subCategoryCode, serialNo…
            </p>
            <input
              ref="iussImportFile"
              type="file"
              accept=".xlsx,.xls,.csv,.json"
              class="d-none"
              @change="handleIussCategoryImportFile"
            />
            <hr class="my-1" />
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col md="12">
            <b-form-group
              label="Category name"
              invalid-feedback="Category name is required."
            >
              <b-form-input
                ref="name"
                placeholder="Enter name"
                v-model="myObj.name"
              />
            </b-form-group>
          </b-col>

          <template v-if="isInventoryIuss">
            <b-col md="6">
              <b-form-group label="Br. Code">
                <b-form-input
                  v-model="myObj.brCode"
                  placeholder="e.g. 036"
                  @input="syncIussFullCode"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="
                  Number(myObj.parentCategoryID) > 0
                    ? 'Sub Code'
                    : 'Category Code'
                "
              >
                <b-form-input
                  v-model="myObj.code"
                  :placeholder="
                    Number(myObj.parentCategoryID) > 0 ? 'e.g. 0001' : 'e.g. 001'
                  "
                  @input="syncIussFullCode"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Parent Category">
                <v-select
                  v-model="myObj.parentCategoryID"
                  :options="parentCategorySelectOptions"
                  :reduce="(opt) => opt.id"
                  label="name"
                  :clearable="false"
                  placeholder="Select parent"
                  @input="syncIussFullCode"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Serial No">
                <b-form-input
                  v-model="myObj.serialNo"
                  placeholder="e.g. 00001"
                  @input="syncIussFullCode"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Full Code">
                <b-form-input
                  :value="myObj.fullCode || iussFullCodePreview"
                  readonly
                  plaintext
                  class="font-weight-bold"
                />
              </b-form-group>
            </b-col>
          </template>

          <b-col md="12">
            <b-form-group
              label="Users"
              invalid-feedback="Users is required."
              ref="users"
            >
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="userIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="allUsers"
                :clearable="false"
                :reduce="(val) => val.id"
                label="name"
                placeholder="Select user"
              />
            </b-form-group>
          </b-col>

          <b-col md="12" class="mt-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              @click="addCat()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else> {{ sidebarButton }} </span>
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-3"
          show-empty
          :items="sortedCategories"
          :fields="visibleCatFields"
          responsive
          hover
          small
        >
          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(name)="data">
            <span>
              {{
                isInventoryIuss && !isIussParentCategory(data.item)
                  ? "↳ "
                  : ""
              }}{{ data.value }}
            </span>
          </template>

          <template #cell(catType)="data">
            <b-badge
              :variant="
                isIussParentCategory(data.item)
                  ? 'light-primary'
                  : 'light-info'
              "
            >
              {{ iussCategoryTypeLabel(data.item) }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="text-center">
              <b-button
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="openCat(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="delCat(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-warehouse"
      bg-variant="white"
      v-model="visibility2"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Manage Warehouse</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility2 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="d-flex align-items-center">
          <b-col md="10">
            <b-form-group
              label="Name"
              invalid-feedback="Warehouse name is required."
            >
              <b-form-input
                ref="wname"
                v-model="wObj.name"
                placeholder="Enter warehouse name here"
                @focusout="checkWName()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="2">
            <b-button
              variant="success"
              class="btn-icon mr-0 mr-sm-1 mb-1 mb-sm-0"
              @click="saveWare()"
              :disabled="request"
            >
              <b-spinner v-if="request" small type="grow" />
              <feather-icon v-else :icon="buttonIcon" />
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-1"
          show-empty
          :items="warehouses"
          :fields="wFields"
          responsive
          hover
          small
        >
          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="text-center">
              <b-button
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="editWare(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteWare(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-supplier"
      bg-variant="white"
      v-model="visibility3"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Manage Vendors</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility3 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="">
          <b-col md="12">
            <b-form-group
              label="Name"
              invalid-feedback="Vendor name is required."
            >
              <b-form-input
                ref="sname"
                v-model="sObj.name"
                placeholder="Enter vendor name here"
                @focusout="checkSuppName()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group
              label="Contact"
              invalid-feedback="Contact is required."
            >
              <vue-tel-input
                v-model="sObj.contact"
                :defaultCountry="$store.state.countryCode"
                :dropdownOptions="dropOptions"
              ></vue-tel-input>
            </b-form-group>
          </b-col>
          <b-col md="12" class="mt-1">
            <b-button
              variant="success"
              @click="saveSupp()"
              :disabled="request"
              block
            >
              <b-spinner v-if="request" small type="grow" />
              <span v-else>{{$t('Save')}}</span>
            </b-button>
          </b-col>
        </b-row>
        <b-table
          class="mt-2"
          show-empty
          :items="suppliers"
          :fields="spFields"
          responsive
          hover
          small
        >
          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div class="text-center">
              <b-button
                variant="primary"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="editSupp(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                variant="outline-danger"
                class="btn-icon mr-0 ml-sm-1 mb-1 mb-sm-0"
                @click="deleteSupp(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-stock-update"
      bg-variant="white"
      v-model="visibility4"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Update Stock</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility4 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="">
          <b-col md="12">
            <b-form-group
              label="Purchase Order ID"
              invalid-feedback="Purchase Order ID is required."
            >
              <b-form-input
                ref="updID"
                type="number"
                v-model="grnObj.quotationID"
                placeholder="Enter purchase order ID here"
                :disabled="poLoading || poItems.length > 0"
                @keyup.enter="checkQID()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>

          <b-col v-if="poLoading" md="12" class="text-center mt-1">
            <b-spinner type="grow" variant="primary" />
          </b-col>

          <template v-else-if="poItems.length > 0">
            <b-col md="12">
              <b-form-group
                label="Date"
                invalid-feedback="Date is required"
                ref="updDate"
              >
                <flat-pickr
                  :config="config"
                  v-model="grnObj.date"
                  class="form-control"
                  :placeholder="$t('Select Date')"
                  @on-change="CheckUpdateDate()"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                label="Delivery Charges"
                invalid-feedback="Please enter valid amount"
                ref="delCharge"
              >
                <b-form-input
                  type="number"
                  v-model="grnObj.deliveryCharges"
                  placeholder="Enter delivery charges"
                  @input="CheckDelCharges()"
                >
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Note" ref="updNote">
                <b-form-textarea
                  v-model.trim="grnObj.narration"
                  placeholder="Enter note"
                />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-table
                :items="poItems"
                :fields="poFields"
                small
                responsive
                fixed
                class="mt-1"
              >
                <template #cell(product)="data">
                  {{ data.item.name }} <br />
                  qty: {{ data.item.requestedQty }}
                </template>
                <template #cell(updateQty)="data">
                  <b-form-input
                    type="number"
                    v-model="data.item.finalQty"
                    placeholder="Enter quantity"
                  >
                  </b-form-input>
                </template>
              </b-table>
            </b-col>
            <b-col md="12" class="mt-1">
              <b-button
                variant="success"
                block
                :disabled="stockUpdating"
                @click="updateStock(false)"
              >
                <b-spinner
                  v-if="stockUpdating && !printLoading"
                  small
                  type="grow"
                  class="mr-50"
                />
                <span>Update</span>
              </b-button>
            </b-col>
            <b-col md="12" class="mt-1">
              <b-button
                variant="warning"
                block
                :disabled="stockUpdating"
                @click="updateStock(true)"
              >
                <b-spinner
                  v-if="stockUpdating && printLoading"
                  small
                  type="grow"
                  class="mr-50"
                />
                <span>Update & Print</span>
              </b-button>
            </b-col>
          </template>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-single-stock-update"
      bg-variant="white"
      v-model="visibility5"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Update Stock - {{ singleObj.name }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility5 = false"
        />
      </div>

      <b-form class="p-2" @submit.prevent>
        <b-row class="">
          <b-col md="12">
            <b-form-group
              :label="`Quantity (${singleObj.currentQuantity})`"
              invalid-feedback="Quantity is required."
              ref="single_qty"
            >
              <b-form-input
                type="number"
                v-model="singleObj.quantity"
                placeholder="Enter quantity here"
                @input="checkSingleQty()"
              >
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Note" ref="single_note">
              <b-form-textarea
                v-model.trim="singleObj.narration"
                placeholder="Enter note"
              />
            </b-form-group>
          </b-col>

          <b-col md="12" class="mt-1">
            <b-button
              variant="success"
              block
              :disabled="singleUpdating"
              @click="updateSingleStock()"
            >
              <b-spinner
                v-if="singleUpdating"
                small
                type="grow"
                class="mr-50"
              />
              <span>Update</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>

    <b-sidebar
      id="sidebar-low-stock-level"
      bg-variant="white"
      v-model="visibility6"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Low stock level</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="visibility6 = false"
        />
      </div>
      <b-form class="p-2">
        <p class="text-muted small">
          Applies to all inventory items. Items with quantity at or below this
          level are marked as low stock in the list.
        </p>
        <b-form-group
          label="Low stock quantity"
          invalid-feedback="Enter a valid number (0 or greater)."
        >
          <b-form-input
            ref="lowStockLevelInput"
            type="number"
            min="0"
            v-model.number="lowStockLevelDraft"
            placeholder="e.g. 10"
          />
        </b-form-group>
        <b-button
          variant="success"
          block
          :disabled="savingLowStockLevel"
          @click="saveLowStockLevelSetting()"
        >
          <b-spinner v-if="savingLowStockLevel" small type="grow" />
          <span v-else>{{ $t("Save") }}</span>
        </b-button>
      </b-form>
      <sidebar-content />
    </b-sidebar>

    <b-sidebar
      id="sidebar-stock-import"
      bg-variant="white"
      v-model="importSidebarVisible"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Import Stock") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="importSidebarVisible = false"
        />
      </div>

      <div class="p-2">
        <p class="text-muted small mb-1">
          {{
            $t(
              "Use names for category, vendor, and warehouse. Rows without a warehouse use the default location below."
            )
          }}
        </p>
        <p v-if="isInventoryIuss" class="text-muted small mb-1">
          Optional IUSS columns: brand, warranty (or warantty), purchaseDate,
          specification (or spacification).
        </p>

        <b-form-group
          :label="$t('Default warehouse (location)')"
          class="mb-1"
        >
          <v-select
            v-model="importWarehouseID"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="warehouses"
            :reduce="(val) => val.id"
            label="name"
            :clearable="false"
            :placeholder="$t('Select warehouse')"
          />
        </b-form-group>

        <b-button variant="outline-secondary" block @click="downloadStockImportSample">
          <feather-icon icon="DownloadIcon" class="mr-50" />
          {{ $t("Download Sample File") }}
        </b-button>

        <hr class="my-2" />

        <b-button
          variant="primary"
          block
          @click="$refs.stockImportFile.click()"
          :disabled="importLoading"
        >
          <feather-icon icon="UploadIcon" class="mr-50" />
          {{ $t("Upload Excel File") }}
        </b-button>
        <input
          ref="stockImportFile"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="handleStockImportFile"
        />

        <div
          v-if="stockImportRows.length > 0"
          class="mt-2 d-flex align-items-center flex-wrap"
        >
          <b-badge variant="success" style="font-size: 14px; padding: 8px 16px">
            {{ stockImportRows.length }} {{ $t("records ready") }}
          </b-badge>
          <b-button
            variant="outline-danger"
            size="sm"
            class="btn-icon ml-1"
            :disabled="importLoading"
            @click="clearStockImport()"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>

        <div v-if="stockImportValidationErrors.length > 0" class="mt-2">
          <b-alert show variant="danger" class="p-1 mb-0">
            <div class="font-weight-bold mb-50">
              {{ stockImportValidationErrors.length }}
              {{ $t("row(s) have validation errors") }}:
            </div>
            <ul
              class="mb-0 pl-1"
              style="max-height: 120px; overflow-y: auto; font-size: 12px"
            >
              <li v-for="(err, i) in stockImportValidationErrors" :key="i">
                {{ $t("Row") }} {{ err.row }}: {{ err.message }}
              </li>
            </ul>
          </b-alert>
        </div>

        <b-button
          v-if="
            stockImportRows.length > 0 &&
            !importLoading &&
            stockImportValidationErrors.length === 0
          "
          variant="success"
          block
          class="mt-2"
          @click="runStockImport()"
        >
          <feather-icon icon="CheckIcon" class="mr-50" />
          {{ $t("Import Now") }}
        </b-button>

        <div v-if="importLoading" class="mt-2">
          <div class="font-weight-bold mb-50">
            {{ $t("Importing") }}… {{ importBatchCurrent }} /
            {{ importBatchTotal }}
          </div>
          <b-progress
            :value="importProgressPercent"
            max="100"
            height="10px"
            variant="primary"
            animated
            show-progress
          />
        </div>

        <div v-if="importResultSummary" class="mt-2 stock-import-result">
          <b-alert show variant="success" class="stock-import-result__alert mb-1">
            <div class="stock-import-result__title">{{ $t("Import complete") }}</div>
            <div class="stock-import-result__stats">
              {{ $t("Imported") }}:
              <strong>{{ importResultSummary.importedCount }}</strong>
              · {{ $t("Skipped") }}:
              <strong>{{ importResultSummary.skippedCount }}</strong>
            </div>
          </b-alert>

          <div
            v-if="importCreatedItems.length > 0"
            class="stock-import-created"
          >
            <div class="stock-import-created__heading">
              {{ $t("Also created on import") }}
            </div>
            <ul class="stock-import-created__list mb-0">
              <li
                v-for="(item, i) in importCreatedItems"
                :key="`${item.kind}-${item.name}-${i}`"
              >
                <span class="stock-import-created__kind">
                  {{ stockImportCreatedKindLabel(item.kind) }}:
                </span>
                <span class="stock-import-created__name">{{ item.name }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </b-sidebar>

    <b-card>
      <b-row align-h="end" class="text-right">
        <b-col class="mt-xl-0 mt-lg-0 mt-md-0 mt-sm-1 mt-1" cols="11">
          <b-dropdown
            class="mx-25 mb-25"
            v-for="(d, ind) in sortedCategories"
            :key="ind"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            split
            :text="iussCategoryLabel(d)"
            :variant="current == d.id ? 'primary' : 'outline-primary'"
            right
            @click="LoadData(d.id)"
          >
            <b-dropdown-item @click="openCat(d)"> Edit </b-dropdown-item>
            <b-dropdown-item @click="delCat(d.id)"> Delete </b-dropdown-item>

            <b-dropdown-divider />
          </b-dropdown>
        </b-col>
        <b-col class="mt-xl-0 mt-lg-0 mt-md-0 mt-sm-1 mt-1 mr-1" cols="*">
          <b-button
            @click="openCat(0)"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="btn-icon rounded-circle"
            v-b-tooltip.hover.top
            title="Manage Categories"
          >
            <feather-icon icon="PlusIcon" />
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mt-1 align-items-center inv-toolbar-row">
        <b-col
          cols="12"
          class="d-flex flex-wrap flex-lg-nowrap align-items-center inv-toolbar"
        >
          <b-button
            v-if="rights.add"
            @click="open(0)"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="inv-toolbar__btn mr-1 mb-1 mb-lg-0"
          >
            <feather-icon icon="PlusIcon" class="mr-50" />
            <span class="align-middle">Inventory</span>
          </b-button>
          <b-button
            v-if="isInventoryIuss && rights.add"
            variant="outline-primary"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="inv-toolbar__btn mr-1 mb-1 mb-lg-0"
            @click="openCat(0)"
          >
            <feather-icon icon="FolderIcon" class="mr-50" />
            <span class="align-middle">Categories / Import</span>
          </b-button>
          <b-button
            v-if="showStockImportButton && rights.add"
            variant="outline-primary"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="inv-toolbar__btn mr-1 mb-1 mb-lg-0"
            @click="openStockImport()"
          >
            <feather-icon icon="UploadIcon" class="mr-50" />
            <span class="align-middle">{{ $t("Import") }}</span>
          </b-button>
          <b-form-group class="mb-0 inv-toolbar__search mb-1 mb-lg-0">
            <b-input-group>
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('Search') + '...'"
              />
            </b-input-group>
          </b-form-group>
          <b-button
            variant="outline-primary"
            v-b-tooltip.hover.top
            :title="$t('Print Report')"
            class="btn-icon inv-toolbar__action mb-1 mb-lg-0 ml-1"
            :disabled="printingReport || !categories.length"
            @click="openInventoryPrintReport()"
          >
            <b-spinner v-if="printingReport" small type="grow" />
            <feather-icon v-else icon="PrinterIcon" />
          </b-button>
          <b-button
            variant="outline-success"
            v-b-tooltip.hover.top
            :title="$t('Export')"
            class="btn-icon inv-toolbar__action mb-1 mb-lg-0 ml-50"
            :disabled="exporting || !categories.length"
            @click="exportInventoryReport()"
          >
            <b-spinner v-if="exporting" small type="grow" />
            <feather-icon v-else icon="DownloadIcon" />
          </b-button>
          <b-button
            v-if="rights.edit"
            variant="outline-primary"
            v-b-tooltip.hover.top
            title="Update Stock"
            class="btn-icon inv-toolbar__action mb-1 mb-lg-0 ml-50"
            @click="openStockUpdate()"
          >
            <feather-icon icon="ArrowUpIcon" />
          </b-button>
          <b-button
            v-if="rights.edit"
            variant="outline-warning"
            v-b-tooltip.hover.top
            title="Low stock level (all items)"
            class="btn-icon inv-toolbar__action mb-1 mb-lg-0 ml-50"
            @click="openLowStockSettings()"
          >
            <feather-icon icon="AlertTriangleIcon" />
          </b-button>
        </b-col>
      </b-row>

      <b-table
        class="mt-1"
        ref="selectableTable"
        :items="filters"
        :fields="visibleInventoryFields"
        :current-page="currentPage"
        :per-page="perPage"
        :busy="dataLoading"
        show-empty
        responsive
        hover
        small
      >
        <template #table-busy>
          <div class="d-flex justify-content-center mb-3 mt-5">
            <b-spinner
              style="width: 3rem; height: 3rem"
              type="grow"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </div>
        </template>
        <template #cell(index)="data">
          <b-badge variant="light-primary">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </b-badge>
        </template>
        <template #cell(date)="data">
          <b-badge variant="light-primary">
            {{
              new Date(data.value).toLocaleDateString("en-UK", {
                year: "numeric",
                day: "numeric",
                month: "short",
              })
            }}
          </b-badge>
        </template>
        <template #cell(image)="data">
          <b-img
            v-if="data.value"
            :src="getInvImagePreviewUrl(data.value)"
            height="60px"
            width="60px"
            style="border-radius: 10px"
            alt="pic"
            referrerpolicy="no-referrer"
            @error="onInvTableImageError($event, data.value)"
          />
          <b-img
            v-else
            src="https://placehold.co/60x60?text=Image"
            height="60px"
            width="60px"
            style="border-radius: 10px"
            alt="pic"
          />
        </template>
        <template #cell(name)="data">
          <b-badge variant="light-primary">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(supplierID)="data">
          <b-badge variant="light-primary">
            {{ getSuppName(data.value) }}
          </b-badge>
        </template>
        <template #cell(quantity)="data">
          <b-badge
            :variant="isLowStock(data.item) ? 'light-danger' : 'light-primary'"
            v-b-tooltip.hover
            :title="
              isLowStock(data.item)
                ? `Low stock (quantity at or below ${globalLowStockLevel})`
                : ''
            "
          >
            {{ data.value }}
            <feather-icon
              v-if="isLowStock(data.item)"
              icon="AlertTriangleIcon"
              size="14"
              class="ml-25 align-middle"
            />
          </b-badge>
        </template>
        <template #cell(sellingPrice)="data">
          <b-badge variant="light-primary">
            {{ data.value }}
          </b-badge>
        </template>
        <template #cell(brand)="data">
          <span>{{ data.value || "-" }}</span>
        </template>
        <template #cell(warranty)="data">
          <span>{{ data.value || "-" }}</span>
        </template>
        <template #cell(purchaseDate)="data">
          <span>{{ formatIussPurchaseDate(data.value) }}</span>
        </template>
        <template #cell(specification)="data">
          <span
            class="d-inline-block text-truncate"
            style="max-width: 160px"
            :title="data.value || ''"
          >
            {{ data.value || "-" }}
          </span>
        </template>

        <template #head(actions)="data">
          <div class="ml-2">
            <span>{{ data.label }}</span>
          </div>
        </template>

        <template #cell(actions)="data">
          <div class="d-flex">
            <b-button
              v-if="rights.edit"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              v-b-tooltip.hover.left
              title="Update Stock"
              class="btn-icon mr-1"
              @click="openSingleUpdate(data.item)"
            >
              <feather-icon icon="ArrowUpCircleIcon" />
            </b-button>

            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              v-b-tooltip.hover.left
              title="View Logs"
              class="btn-icon mr-1"
              @click="viewLogs(data.item)"
            >
              <feather-icon icon="FileTextIcon" />
            </b-button>

            <b-button
              v-if="rights.edit"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              v-b-tooltip.hover.left
              title="Edit"
              class="btn-icon mr-1"
              @click="open(data.item)"
            >
              <feather-icon icon="EditIcon" class="" />
            </b-button>

            <b-button
              v-if="rights.edit && bundleObj && bundleObj.valueBool"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="success"
              v-b-tooltip.hover.left
              title="Create Bundle"
              class="btn-icon mr-1"
              @click="openBundle(data.item)"
              :disabled="bundleDataLoading"
            >
              <b-spinner
                v-if="
                  bundleDataLoading &&
                  selectedProduct &&
                  data.item.id === selectedProduct.id
                "
                small
              />
              <feather-icon v-else icon="PackageIcon" class="" />
            </b-button>

            <b-button
              v-if="data.item.isHold"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              v-b-tooltip.hover.left
              title="Unhold Stock"
              class="btn-icon mr-1"
              @click="unhold(data.item)"
            >
              <feather-icon icon="CheckIcon" class="" />
            </b-button>
            <b-button
              v-else
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-danger"
              v-b-tooltip.hover.left
              title="Hold Stock"
              class="btn-icon mr-1"
              @click="openHold(data.item)"
            >
              <feather-icon icon="XIcon" class="" />
            </b-button>

            <b-button
              v-if="rights.delete"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="outline-danger"
              v-b-tooltip.hover.right
              title="Delete"
              class="btn-icon"
              @click="delProduct(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
      </b-table>
      <b-col cols="12" class="d-flex align-items-center justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="filters.length"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </b-col>
    </b-card>
  </div>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BLink,
  BBadge,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BMediaAside,
  BSpinner,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BFormTextarea,
  BInputGroup,
  BDropdown,
  BDropdownItem,
  BProgress,
  BAlert,
} from "bootstrap-vue";
import * as XLSX from "xlsx";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions, mapGetters } from "vuex";
import {
  getUploadPath,
  uploadToMySkool,
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";
import {
  buildFormattedWorksheet,
  writeWorkbookToFile,
} from "@/utils/excelExport";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BPagination,
    BImg,
    BFormCheckbox,
    BFormGroup,
    BForm,
    BMediaAside,
    BSpinner,
    BSidebar,
    BInputGroupPrepend,
    BFormTextarea,
    BInputGroup,
    BDropdown,
    BDropdownItem,
    BProgress,
    BAlert,
    VBTooltip,
    VBToggle,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
    Ripple,
  },
  data() {
    return {
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d\\TH:i:S",
      },
      dropOptions: {
        disabled: false,
        showDialCodeInList: true,
        showFlags: true,
      },
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      rights: {},
      request: false,
      dataLoading: false,
      exporting: false,
      printingReport: false,
      searchQuery: "",
      currentPage: 1,
      perPage: 30,
      visibility: false,
      visibility1: false,
      visibility2: false,
      visibility3: false,
      visibility4: false,
      visibility5: false,
      visibility6: false,
      lowStockSetting: null,
      lowStockLevelDraft: 0,
      savingLowStockLevel: false,
      fields: [
        { label: "S.No", key: "index" },
        { label: "image", key: "image" },
        { label: "name", key: "name" },
        { label: "vendor", key: "supplierID" },
        { label: "quantity", key: "quantity" },
        { label: "selling price", key: "sellingPrice" },
        { key: "actions", label: "actions" },
      ],
      items: [],
      myObj: {},
      wObj: {},
      warehouses: [],
      wFields: [
        { label: "name", key: "name" },
        { key: "actions", label: "actions" },
      ],
      catFields: [
        { label: "name", key: "name" },
        { key: "actions", label: "actions" },
      ],
      buttonIcon: "",
      sObj: {},
      suppliers: [],
      spFields: [
        { label: "name", key: "name" },
        { label: "contact", key: "contact" },
        { key: "actions", label: "actions" },
      ],
      categories: [],
      iussParentCategories: [],
      iussSubCategories: [],
      current: 0,
      invObj: {},
      imagePreview: "",
      docLoading: false,
      mediaLoaded: false,
      sidebarButton: "",
      sidebarTitle: "",
      holding: false,
      holdObj: {},
      userIDs: [],
      allUsers: [],
      classesOptions: [],
      selectedClasses: [],
      selectedCategories: [],
      bundleObj: null,
      allProducts: [],
      selectedProduct: null,
      bundleItems: [],
      bundleSaving: false,
      bundleDataLoading: false,
      poLoading: false,
      poItems: [],
      poFields: [
        { label: "Product", key: "product" },
        { label: "Upd. Qty", key: "updateQty" },
      ],
      stockUpdating: false,
      printLoading: false,
      grnObj: {
        quotationID: "",
        date: null,
        deliveryCharges: 0,
        vendorID: 0,
        narration: "",
        createdBy: this.$store.state.userData.userID,
      },
      singleObj: {
        stockID: 0,
        name: "",
        currentQuantity: 0,
        quantity: "",
        narration: "",
      },
      singleUpdating: false,
      accObj: null,
      accList: [],
      accLoading: false,
      invSetting: null,
      inventoryIussSetting: null,
      iussImportOptions: null,
      iussImportLoading: false,
      showStockImportButton: true,
      importSidebarVisible: false,
      importWarehouseID: 0,
      stockImportRows: [],
      stockImportValidationErrors: [],
      importLoading: false,
      importBatchCurrent: 0,
      importBatchTotal: 0,
      importResultSummary: null,
      importCreatedItems: [],
      stockImportBatchSize: 100,
    };
  },
  computed: {
    hideSellingPrice() {
      return this.reportDomain === "myskoolpublicschool";
    },
    isInventoryIuss() {
      const s = this.inventoryIussSetting;
      if (!s) return false;
      if (s.valueBool === true) return true;
      const v = s.value;
      return v === true || v === 1 || String(v).toLowerCase() === "true" || String(v) === "1";
    },
    globalLowStockLevel() {
      const s = this.lowStockSetting;
      if (!s) return 0;
      const n = Number(s.valueNumber != null ? s.valueNumber : s.value);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    },
    visibleCatFields() {
      if (!this.isInventoryIuss) return this.catFields;
      return [
        { label: "name", key: "name" },
        { label: "Br. Code", key: "brCode" },
        { label: "Code", key: "code" },
        { label: "Full Code", key: "fullCode" },
        { label: "Type", key: "catType" },
        { key: "actions", label: "actions" },
      ];
    },
    parentCategoryOptions() {
      if (this.isInventoryIuss && (this.iussParentCategories || []).length) {
        return this.iussParentCategories;
      }
      const list = this.categories || [];
      return list.filter(
        (c) => !c.parentCategoryID || Number(c.parentCategoryID) === 0
      );
    },
    parentCategorySelectOptions() {
      const currentId = this.myObj && this.myObj.id ? this.myObj.id : null;
      const parents = this.parentCategoryOptions.filter(
        (c) => !currentId || c.id !== currentId
      );
      return [{ id: 0, name: "None (Main Category)" }].concat(parents);
    },
    sortedCategories() {
      const list = (this.categories || []).slice();
      if (!this.isInventoryIuss) {
        return list.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base",
          })
        );
      }
      const parents = (
        (this.iussParentCategories || []).length
          ? this.iussParentCategories
          : list.filter(
              (c) => !c.parentCategoryID || Number(c.parentCategoryID) === 0
            )
      )
        .slice()
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base",
          })
        );
      const subs = (
        (this.iussSubCategories || []).length
          ? this.iussSubCategories
          : list.filter(
              (c) => c.parentCategoryID && Number(c.parentCategoryID) !== 0
            )
      ).slice();
      const result = [];
      const used = {};
      parents.forEach((p) => {
        result.push(p);
        used[p.id] = true;
        subs
          .filter((c) => Number(c.parentCategoryID) === Number(p.id))
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          )
          .forEach((s) => {
            result.push(s);
            used[s.id] = true;
          });
      });
      list.forEach((c) => {
        if (!used[c.id]) result.push(c);
      });
      return result;
    },
    categorySelectOptions() {
      return this.sortedCategories.map((c) => {
        return Object.assign({}, c, {
          displayName: this.formatCategoryOptionLabel(c),
        });
      });
    },
    iussFullCodePreview() {
      if (!this.isInventoryIuss) return "";
      const br = String(this.myObj.brCode || "").trim();
      const code = String(this.myObj.code || "").trim();
      const serial = String(this.myObj.serialNo || "").trim();
      const parentId = Number(this.myObj.parentCategoryID) || 0;
      if (!parentId) {
        return [br, code, "0000", serial || "00000"].filter((x) => x !== "").join("-");
      }
      const parent = (this.iussParentCategories || [])
        .concat(this.categories || [])
        .find((c) => Number(c.id) === parentId);
      const parentCode = parent ? String(parent.code || "").trim() : "";
      return [br, parentCode, code, serial].filter((x) => x !== "").join("-");
    },
    visibleInventoryFields() {
      let list = this.hideSellingPrice
        ? this.fields.filter((f) => f.key !== "sellingPrice")
        : this.fields.slice();
      if (this.isInventoryIuss) {
        const actions = list.find((f) => f.key === "actions");
        const withoutActions = list.filter((f) => f.key !== "actions");
        withoutActions.push(
          { label: "brand", key: "brand" },
          { label: "warranty", key: "warranty" },
          { label: "purchase date", key: "purchaseDate" },
          { label: "specification", key: "specification" }
        );
        if (actions) withoutActions.push(actions);
        return withoutActions;
      }
      return list;
    },
    importProgressPercent() {
      if (!this.importBatchTotal) return 0;
      return Math.min(
        100,
        Math.round((this.importBatchCurrent / this.importBatchTotal) * 100)
      );
    },
    filters() {
      return this.items.filter((pro) => {
        return pro.name.toLowerCase().match(this.searchQuery.toLowerCase());
      });
    },
    availableProducts() {
      return this.allProducts.filter(
        (item) => item.id !== this.selectedProduct?.id
      );
    },
    invImagePreview() {
      const src = this.imagePreview || this.invObj.image || "";
      if (!src) return "";
      if (String(src).startsWith("blob:")) return src;
      return getMediaPreviewUrl(src, "Image");
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.LoadCat(0);
      this.LoadWare();
      this.LoadSupplier();
      this.LoadUsers();
      this.LoadClasses();
      this.LoadSettings();
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    getInvImagePreviewUrl(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    onInvImagePreviewError(event) {
      const url = this.imagePreview || this.invObj.image;
      if (!url || String(url).startsWith("blob:")) return;
      handleDrivePreviewError(event, url, "image");
    },

    onInvTableImageError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },

    clearImagePreview() {
      if (this.imagePreview && String(this.imagePreview).startsWith("blob:")) {
        URL.revokeObjectURL(this.imagePreview);
      }
      this.imagePreview = "";
    },

    formatIussPurchaseDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return dd + "/" + mm + "/" + yyyy;
    },

    viewLogs(item) {
      const url =
        `https://${this.reportDomain}.myskool.app/Inventory/Show?` +
        `type=stock-log` +
        `&dbb=${this.$store.state.userData.db}` +
        `&cID=${item.campusID}` +
        `&stockID=${item.id}` +
        `&dtFrom=&dtTo=`;

      window.open(url, "_blank");
    },

    getCurrentCategoryName() {
      if (!this.current) return "All Categories";
      const cat = (this.categories || []).find((c) => c.id == this.current);
      return (cat && cat.name) || String(this.current);
    },

    openInventoryPrintReport() {
      if (!(this.categories || []).length) {
        this.$bvToast.toast("No inventory categories to print.", {
          title: "Info",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.printingReport = true;
      try {
        const routeData = this.$router.resolve({
          name: "inventory-stock-print",
          query: {
            db: this.$store.state.userData.db,
            cID: String(this.$store.state.userData.cId),
            catID: "0",
            categoryName: "All Categories",
            search: this.searchQuery || "",
            iuss: this.isInventoryIuss ? "1" : "0",
          },
        });
        window.open(routeData.href, "_blank");
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Failed to open inventory print report.", {
          title: "Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.printingReport = false;
      }
    },

    async fetchCategoryWiseStockRows() {
      const db = this.$store.state.userData.db;
      const cID = this.$store.state.userData.cId;
      const token = this.$store.state.userData.token;
      const search = String(this.searchQuery || "")
        .trim()
        .toLowerCase();
      const categories = (this.categories || []).slice().sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      );

      const rows = [];
      let sno = 0;

      for (let i = 0; i < categories.length; i += 1) {
        const cat = categories[i];
        const res = await this.get({
          url:
            this.$store.state.domain +
            "posStock/LoadByCat?db=" +
            db +
            "&cID=" +
            cID +
            "&catID=" +
            cat.id,
          token,
        });
        let items = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
        if (search) {
          items = items.filter((item) =>
            String(item.name || "")
              .toLowerCase()
              .includes(search)
          );
        }
        items
          .slice()
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
            })
          )
          .forEach((item) => {
            sno += 1;
            const qty = item.quantity != null ? Number(item.quantity) : 0;
            const purchasePrice =
              item.purchasePrice != null ? Number(item.purchasePrice) : 0;
            const sellingPrice =
              item.sellingPrice != null ? Number(item.sellingPrice) : 0;
            const row = {
              sno,
              category: cat.name || "",
              name: item.name || "",
              sku: item.sku || "",
              vendor: this.getSuppName(item.supplierID) || "",
              quantity: qty,
            };
            if (!this.hideSellingPrice) {
              row.purchasePrice = purchasePrice;
              row.purchaseValue = qty * purchasePrice;
              row.sellingPrice = sellingPrice;
              row.sellingValue = qty * sellingPrice;
            }
            if (this.isInventoryIuss) {
              row.brand = item.brand || "";
              row.warranty = item.warranty || "";
              row.purchaseDate = this.formatIussPurchaseDate(item.purchaseDate);
              if (row.purchaseDate === "-") row.purchaseDate = "";
              row.specification = item.specification || "";
            }
            rows.push(row);
          });
      }

      return rows;
    },

    async exportInventoryReport() {
      if (!(this.categories || []).length) {
        this.$bvToast.toast("No inventory categories to export.", {
          title: "Info",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.exporting = true;
      try {
        const rows = await this.fetchCategoryWiseStockRows();
        if (!rows.length) {
          this.$bvToast.toast("No inventory items to export.", {
            title: "Info",
            variant: "warning",
            toaster: "b-toaster-top-center",
          });
          return;
        }

        const columns = [
          { key: "sno", label: "S.No" },
          { key: "category", label: "Category" },
          { key: "name", label: "Item Name" },
          { key: "sku", label: "SKU" },
          { key: "vendor", label: "Vendor" },
        ];
        if (this.isInventoryIuss) {
          columns.push({ key: "brand", label: "Brand" });
          columns.push({ key: "warranty", label: "Warranty" });
          columns.push({ key: "purchaseDate", label: "Purchase Date" });
          columns.push({ key: "specification", label: "Specification" });
        }
        columns.push({ key: "quantity", label: "Qty" });
        if (!this.hideSellingPrice) {
          columns.push({ key: "sellingPrice", label: "Selling Price" });
          columns.push({ key: "sellingValue", label: "Selling Value" });
          columns.push({ key: "purchasePrice", label: "Purchase Price" });
          columns.push({ key: "purchaseValue", label: "Purchase Value" });
        }

        const stamp = new Date().toISOString().slice(0, 10);
        const searchBit = this.searchQuery
          ? " · Search: " + this.searchQuery
          : "";
        const worksheet = buildFormattedWorksheet(rows, {
          columns,
          title: "Inventory Stock Report",
          subtitle: "Category-wise · " + stamp + searchBit,
        });

        await writeWorkbookToFile(
          worksheet,
          "Inventory_Stock_CategoryWise_" + stamp + ".xlsx",
          "Inventory"
        );

        this.$bvToast.toast(
          "Exported " + rows.length + " inventory items (category-wise).",
          {
            title: this.$t("Success"),
            variant: "success",
            toaster: "b-toaster-top-center",
          }
        );
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Failed to export inventory report.", {
          title: "Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.exporting = false;
      }
    },

    isSelectedItem(item) {
      return this.bundleItems.some(
        (bundleItem) => bundleItem.stockID === item.id
      );
    },

    isLowStock(item) {
      if (!item) return false;
      const level = this.globalLowStockLevel;
      if (level <= 0) return false;
      const qty = Number(item.quantity);
      if (!Number.isFinite(qty)) return false;
      return qty <= level;
    },

    openLowStockSettings() {
      this.lowStockLevelDraft = this.globalLowStockLevel;
      this.visibility6 = true;
    },

    async saveLowStockLevelSetting() {
      const n = Number(this.lowStockLevelDraft);
      if (!Number.isFinite(n) || n < 0) {
        const elem = this.$refs.lowStockLevelInput;
        if (elem) elem.state = false;
        this.$bvToast.toast("Enter a valid low stock level (0 or greater).", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      if (this.$refs.lowStockLevelInput) {
        this.$refs.lowStockLevelInput.state = true;
      }
      let setting = this.lowStockSetting;
      if (!setting) {
        setting = {
          id: 0,
          key: "inventory_low_stock_level",
          value: "",
          valueBool: true,
          valueNumber: n,
          campusID: this.$store.state.userData.cId,
        };
      } else {
        setting = { ...setting, valueNumber: n };
      }
      this.savingLowStockLevel = true;
      const ok = await this.post({
        url:
          this.$store.state.domain +
          "Settings/SaveNew?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [setting],
        message: "Low stock level saved.",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingLowStockLevel = false;
      if (ok) {
        this.lowStockSetting = setting;
        this.visibility6 = false;
      }
    },

    getSuppName(id) {
      let obj = this.suppliers.find((el) => el.id == id);
      return obj ? obj.name : "";
    },

    openStockUpdate() {
      this.visibility4 = true;
      this.poItems = [];

      this.grnObj = {
        id: 0,
        quotationID: "",
        date: null,
        vendorID: 0,
        deliveryCharges: 0,
        total: 0,
        journalEntryID: -1,
        narration: "",
        campusID: this.$store.state.userData.cId,
        createdBy: this.$store.state.userData.userID,
        updatedBy: 0,
        createdOn: null,
        updatedOn: null,
      };
      let elem = this.$refs["updID"];
      elem.state = undefined;
    },
    async checkQID() {
      let x = parseInt(this.grnObj.quotationID);
      if (isNaN(x) || x <= 0) {
        this.$bvToast.toast("Please enter the quotation ID.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      this.grnObj.quotationID = x;
      this.poLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Quotations/LoadDetails?db=" +
          this.$store.state.userData.db +
          "&quotationID=" +
          this.grnObj.quotationID,
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      // console.log(res);
      if (Array.isArray(res) && res.length > 0) {
        this.grnObj.vendorID = res[0].vendorID;
        this.poItems = res.map((el) => ({
          id: 0,
          grnID: 0,
          productID: el.us.productID,
          unitPrice: el.us.unitPrice,
          requestedQty: el.us.qty,
          finalQty: el.us.qty,
          totalPrice: el.us.totalPrice,
          campusID: this.$store.state.userData.cId,
          name: el.pd.name,
        }));
      } else {
        this.$bvToast.toast(this.$t("No records found"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.poItems = [];
      }
      this.poLoading = false;
    },
    CheckUpdateDate() {
      var elem = this.$refs["updDate"];
      if (!this.grnObj.date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckDelCharges() {
      var elem = this.$refs["delCharge"];
      let x = parseInt(this.grnObj.deliveryCharges);
      if (isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        this.grnObj.deliveryCharges = x;
        return (elem.state = true);
      }
    },
    CheckPItems() {
      let state = true;
      this.poItems.forEach((item) => {
        let x = parseInt(item.finalQty);
        if (isNaN(x) || x <= 0) {
          state = false;
        } else {
          item.finalQty = x;
          item.totalPrice = x * item.unitPrice;
        }
      });
      return state;
    },
    async updateStock(isPrint) {
      const c1 = this.CheckUpdateDate();
      const c2 = this.CheckDelCharges();
      const c3 = this.CheckPItems();

      if (!c1) {
        this.$bvToast.toast("Please enter the date.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      if (!c2) {
        this.$bvToast.toast("Please enter correct delivery charges.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      if (!c3) {
        this.$bvToast.toast("Please enter the quantity correctly.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.stockUpdating = true;
      this.printLoading = isPrint;

      const pTotal = this.poItems.reduce(
        (acc, item) => acc + item.totalPrice,
        0
      );
      this.grnObj.total = pTotal + this.grnObj.deliveryCharges;
      const payload = { grn: this.grnObj, details: this.poItems };
      // console.log(payload);

      let obj = {
        url:
          this.$store.state.domain +
          "goodsReceivingNote/Save?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: payload,
        message: "Stock updated successfully!",
        context: this,
        showAndReturn: true,
        token: this.$store.state.userData.token,
      };
      let res = await this.post(obj);
      if (res?.data) {
        this.visibility4 = false;
        this.LoadData(this.current);

        if (isPrint) {
          const url =
            `https://${this.reportDomain}.myskool.app/Inventory/GRN?` +
            `dbb=${this.$store.state.userData.db}` +
            `&cID=${this.$store.state.userData.cId}` +
            `&quotationID=${res.data.grn.id}`;

          window.open(url, "_blank");
        }
      }
      this.stockUpdating = false;
      this.printLoading = false;
    },
    openSingleUpdate(item) {
      this.visibility5 = true;
      this.singleObj = {
        stockID: item.id,
        name: item.name,
        currentQuantity: item.quantity,
        quantity: "",
        narration: "",
      };
      let elem = this.$refs["single_qty"];
      elem.state = undefined;
    },
    checkSingleQty() {
      let x = parseInt(this.singleObj.quantity);
      let elem = this.$refs["single_qty"];
      if (isNaN(x) || x == 0 || this.singleObj.currentQuantity + x < 0) {
        return (elem.state = false);
      } else {
        this.singleObj.quantity = x;
        return (elem.state = true);
      }
    },
    async updateSingleStock() {
      const c1 = this.checkSingleQty();

      if (!c1) {
        this.$bvToast.toast("Please enter the quantity.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      this.singleUpdating = true;
      const pData = {
        productData: [
          {
            productID: this.singleObj.stockID,
            quantity: this.singleObj.quantity,
          },
        ],
        date: new Date().toJSON(),
        narration: this.singleObj.narration,
        userID: this.$store.state.userData.userID,
      };
      let obj = {
        url:
          this.$store.state.domain +
          "posStock/updateQuantity?_db=" +
          this.$store.state.userData.db,
        body: pData,
        message: "Stock updated successfully!",
        context: this,
        token: this.$store.state.userData.token,
      };
      let res = await this.post(obj);
      if (res) {
        this.visibility5 = false;
        this.LoadData(this.current);
      }
      this.singleUpdating = false;
    },

    async LoadData(id) {
      this.dataLoading = true;
      this.current = id;
      var obj = {
        url:
          this.$store.state.domain +
          "posStock/LoadByCat?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&catID=" +
          id,
        token: this.$store.state.userData.token,
      };
      this.items = await this.get(obj);
      this.dataLoading = false;
      // this.items = this.products.filter((el) => el.categoryID == id);
    },
    openCat(id) {
      if (id == 0) {
        this.myObj = {
          id: 0,
          name: "",
          allowedUsers: "",
          campusID: this.$store.state.userData.cId,
          brCode: "",
          code: "",
          parentCategoryID: 0,
          serialNo: "",
          fullCode: "",
          parentAccountID: 0,
          expenseAccountID: 0,
        };
        this.userIDs = [];
        this.sidebarButton = "Save";
      } else {
        this.myObj = {
          ...id,
          brCode: id.brCode || "",
          code: id.code || "",
          parentCategoryID: Number(id.parentCategoryID) || 0,
          serialNo: id.serialNo || "",
          fullCode: id.fullCode || "",
          parentAccountID: Number(id.parentAccountID) || 0,
          expenseAccountID: Number(id.expenseAccountID) || 0,
        };
        this.userIDs = this.myObj.allowedUsers
          ? this.myObj.allowedUsers
              .split(",")
              .filter((el) => el)
              .map((el) => parseInt(el))
          : [];
        this.sidebarButton = "Update";
        this.syncIussFullCode();
      }
      // console.log(this.userIDs);
      this.visibility = true;
      if (this.isInventoryIuss) {
        this.LoadIussImportOptions();
      }
      var elem = this.$refs["name"];
      elem.state = undefined;
    },
    syncIussFullCode() {
      if (!this.isInventoryIuss || !this.myObj) return;
      this.myObj.fullCode = this.iussFullCodePreview;
      if (
        this.myObj.parentCategoryID === null ||
        this.myObj.parentCategoryID === undefined ||
        this.myObj.parentCategoryID === ""
      ) {
        this.myObj.parentCategoryID = 0;
      } else {
        this.myObj.parentCategoryID = Number(this.myObj.parentCategoryID) || 0;
      }
      this.myObj.parentAccountID = Number(this.myObj.parentAccountID) || 0;
      this.myObj.expenseAccountID = Number(this.myObj.expenseAccountID) || 0;
    },
    checkName() {
      var elem = this.$refs["name"];
      if (this.myObj.name.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async addCat() {
      if (this.checkName() == true) {
        this.request = true;
        this.myObj.allowedUsers = this.userIDs.join(",");
        if (this.isInventoryIuss) {
          this.syncIussFullCode();
        }

        var status;
        if (this.isInventoryIuss) {
          const body = {
            id: Number(this.myObj.id) || 0,
            name: this.myObj.name,
            code: this.myObj.code || "",
            brCode: this.myObj.brCode || "",
            serialNo: this.myObj.serialNo || "",
            parentCategoryID: Number(this.myObj.parentCategoryID) || 0,
            campusID: this.$store.state.userData.cId,
            allowedUsers: this.userIDs.join(","),
            parentAccountID: Number(this.myObj.parentAccountID) || 0,
            expenseAccountID: Number(this.myObj.expenseAccountID) || 0,
          };
          status = await this.post({
            url:
              this.$store.state.domain +
              "posCategories/Save?db=" +
              this.$store.state.userData.db,
            body,
            message:
              body.id == 0
                ? "Category added successfully!"
                : "Category updated successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else if (this.myObj.id == 0) {
          status = await this.post({
            url:
              this.$store.state.domain +
              "posCategories?db=" +
              this.$store.state.userData.db,
            body: this.myObj,
            message: "Category added successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          status = await this.put({
            url:
              this.$store.state.domain +
              "posCategories/" +
              this.myObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Category updated successfully!",
            context: this,
            body: this.myObj,
            token: this.$store.state.userData.token,
          });
        }
        if (status) {
          this.LoadCat();
          // this.visibility = false;
          var elem = this.$refs["name"];
          elem.state = undefined;
          this.sidebarButton = "Save";
          this.myObj = {
            id: 0,
            name: "",
            allowedUsers: "",
            campusID: this.$store.state.userData.cId,
            brCode: "",
            code: "",
            parentCategoryID: 0,
            serialNo: "",
            fullCode: "",
            parentAccountID: 0,
            expenseAccountID: 0,
          };
          this.userIDs = [];
        }
        this.request = false;
      }
    },
    async delCat(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "posCategories/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Category removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadCat(0);
        // this.categories = this.categories.filter((el) => el.id != id);
      }
    },

    openWare(id) {
      this.buttonIcon = "PlusIcon";
      this.visibility2 = true;

      this.wObj = {
        id: 0,
        name: "",
        campusID: this.$store.state.userData.cId,
      };
      var elem = this.$refs["wname"];
      elem.state = undefined;
    },
    editWare(item) {
      this.wObj = { ...item };
      this.buttonIcon = "Edit2Icon";
      var elem = this.$refs["wname"];
      elem.state = undefined;
    },
    checkWName() {
      var elem = this.$refs["wname"];
      if (this.wObj.name.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveWare() {
      if (this.checkWName() == true) {
        this.request = true;
        if (this.wObj.id == 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "posWarehouse?db=" +
              this.$store.state.userData.db,
            body: this.wObj,
            message: "Warehouse added successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          var status = await this.put({
            url:
              this.$store.state.domain +
              "posWarehouse/" +
              this.wObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Warehouse updated successfully!",
            context: this,
            body: this.wObj,
            token: this.$store.state.userData.token,
          });
        }
        if (status) {
          this.LoadWare();
          var elem = this.$refs["wname"];
          elem.state = undefined;
          this.wObj = {
            id: 0,
            name: "",
            campusID: this.$store.state.userData.cId,
          };
        }
        this.request = false;
      }
    },
    async deleteWare(id) {
      // console.log(acc);
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "posWarehouse/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Warehouse removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadWare();
      }
    },

    openSupp() {
      this.visibility3 = true;

      this.sObj = {
        id: 0,
        name: "",
        contact: "",
        campusID: this.$store.state.userData.cId,
      };
      var elem = this.$refs["sname"];
      elem.state = undefined;
    },
    editSupp(item) {
      this.sObj = { ...item };
      var elem = this.$refs["sname"];
      elem.state = undefined;
    },
    checkSuppName() {
      var elem = this.$refs["sname"];
      if (this.sObj.name.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async saveSupp() {
      if (this.checkSuppName() == true) {
        this.request = true;
        if (this.sObj.id == 0) {
          var status = await this.post({
            url:
              this.$store.state.domain +
              "posSupplier?db=" +
              this.$store.state.userData.db,
            body: this.sObj,
            message: "Vendor added successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
        } else {
          var status = await this.put({
            url:
              this.$store.state.domain +
              "posSupplier/" +
              this.sObj.id +
              "?db=" +
              this.$store.state.userData.db,
            message: "Vendor updated successfully!",
            context: this,
            body: this.sObj,
            token: this.$store.state.userData.token,
          });
        }
        if (status) {
          this.LoadSupplier();
          var elem = this.$refs["sname"];
          elem.state = undefined;
          this.sObj = {
            id: 0,
            name: "",
            contact: "",
            campusID: this.$store.state.userData.cId,
          };
        }
        this.request = false;
      }
    },
    async deleteSupp(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "posSupplier/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Vendor removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadSupplier();
      }
    },

    open(id) {
      if (id == 0) {
        this.invObj = {
          id: 0,
          name: "",
          categoryID: this.current,
          warehouseID: 0,
          supplierID: 0,
          quantity: 0,
          sellingPrice: 0,
          purchasePrice: 0,
          restockQuantity: 0,
          isHold: false,
          holdTill: new Date().toJSON(),
          image: "",
          sku: "",
          campusID: this.$store.state.userData.cId,
          classIDs: "",
          accountID: 0,
          totalValue: 0,
          brand: "",
          warranty: "",
          purchaseDate: null,
          specification: "",
        };
        this.mediaLoaded = false;
        this.clearImagePreview();
        this.selectedClasses = [];
        this.selectedCategories = this.current ? [this.current] : [];
      } else {
        this.clearImagePreview();
        this.invObj = {
          ...id,
          brand: id.brand || "",
          warranty: id.warranty || "",
          purchaseDate: id.purchaseDate || null,
          specification: id.specification || "",
        };
        if (this.invObj.image) this.mediaLoaded = true;
        else this.mediaLoaded = false;

        this.selectedClasses = this.invObj.classIDs
          ? this.invObj.classIDs
              .split(",")
              .filter((el) => el)
              .map(Number)
          : [];

        this.selectedCategories = this.invObj.multiCategoryIDs
          ? this.invObj.multiCategoryIDs
              .split(",")
              .filter((el) => el)
              .map(Number)
          : this.invObj.categoryID
          ? [this.invObj.categoryID]
          : [];
      }

      this.visibility1 = true;
      var elem = this.$refs["image"];
      elem.state = undefined;
      var elem = this.$refs["catID"];
      elem.state = undefined;
      var elem = this.$refs["suppID"];
      elem.state = undefined;
      var elem = this.$refs["wareID"];
      elem.state = undefined;
      var elem = this.$refs["pname"];
      elem.state = undefined;
      var elem = this.$refs["pprice"];
      if (elem) elem.state = undefined;
      var elem = this.$refs["quantity"];
      if (elem) elem.state = undefined;
      var elem = this.$refs["sprice"];
      if (elem) elem.state = undefined;
      var elem = this.$refs["restock"];
      if (elem) elem.state = undefined;
      var elem = this.$refs["sku"];
      elem.state = undefined;
    },
    async addImage() {
      const media = this.$refs.invImage.files[0];
      if (!media) return;

      this.clearImagePreview();
      this.docLoading = true;
      const localPreview = URL.createObjectURL(media);
      this.imagePreview = localPreview;
      this.mediaLoaded = true;

      try {
        const uploaded = await uploadToMySkool([media]);
        const path = getUploadPath(uploaded[0]);
        if (!path) throw new Error("Upload path missing");

        URL.revokeObjectURL(localPreview);
        this.imagePreview = "";
        this.invObj.image = path;
        this.mediaLoaded = true;
      } catch (err) {
        URL.revokeObjectURL(localPreview);
        this.imagePreview = "";
        this.invObj.image = "";
        this.mediaLoaded = false;
        console.log(err);
      } finally {
        this.docLoading = false;
        if (this.$refs.invImage) this.$refs.invImage.value = "";
      }
    },
    removeImage() {
      this.clearImagePreview();
      this.invObj.image = "";
      this.mediaLoaded = false;
      if (this.$refs.invImage) this.$refs.invImage.value = "";
    },
    CheckImage() {
      var elem = this.$refs["image"];
      if (!this.invObj.image) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckCatID() {
      // When inventory_settings is ON, the single-select ref is unmounted (v-if hidden).
      // Validate the multi-select selectedCategories array instead.
      if (this.invSetting && this.invSetting.valueBool) {
        var multiElem = this.$refs["multiCatID"];
        if (!this.selectedCategories || this.selectedCategories.length === 0) {
          if (multiElem) multiElem.state = false;
          return false;
        } else {
          if (multiElem) multiElem.state = true;
          return true;
        }
      }
      var elem = this.$refs["catID"];
      if (!elem) return true; // ref not mounted, skip
      if (this.invObj.categoryID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSuppID() {
      var elem = this.$refs["suppID"];
      if (this.invObj.supplierID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckWareID() {
      var elem = this.$refs["wareID"];
      if (this.invObj.warehouseID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckPName() {
      var elem = this.$refs["pname"];
      if (this.invObj.name.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSKU() {
      var elem = this.$refs["sku"];
      if (this.invObj.sku.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckPP() {
      if (this.hideSellingPrice) return true;
      var elem = this.$refs["pprice"];
      if (!elem) return true;
      var re = /^[0-9]+$/;
      let x = parseInt(this.invObj.purchasePrice);
      if (!re.test(this.invObj.purchasePrice) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckQuantity() {
      var elem = this.$refs["quantity"];
      var re = /^[0-9]+$/;
      let x = parseInt(this.invObj.quantity);
      if (!re.test(this.invObj.quantity) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSP() {
      if (this.hideSellingPrice) return true;
      var elem = this.$refs["sprice"];
      if (!elem) return true;
      var re = /^[0-9]+$/;
      let x = parseInt(this.invObj.sellingPrice);
      if (!re.test(this.invObj.sellingPrice) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckRestock() {
      var elem = this.$refs["restock"];
      var re = /^[0-9]+$/;
      let x = parseInt(this.invObj.restockQuantity);
      if (!re.test(this.invObj.restockQuantity) || isNaN(x) || x < 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async save() {
      if (
        this.CheckCatID() == false ||
        this.CheckSuppID() == false ||
        this.CheckWareID() == false ||
        this.CheckPName() == false ||
        // this.CheckImage() == false ||
        this.CheckSKU() == false ||
        this.CheckPP() == false ||
        this.CheckQuantity() == false ||
        this.CheckSP() == false ||
        this.CheckRestock() == false
      ) {
        this.$bvToast.toast(this.$t("Enter the details correctly!"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-right",
        });
      } else {
        this.request = true;
        this.invObj.quantity = parseInt(this.invObj.quantity);
        this.invObj.sellingPrice = this.hideSellingPrice
          ? 0
          : parseInt(this.invObj.sellingPrice) || 0;
        this.invObj.purchasePrice = this.hideSellingPrice
          ? 0
          : parseInt(this.invObj.purchasePrice) || 0;
        this.invObj.restockQuantity = parseInt(this.invObj.restockQuantity);
        this.invObj.classIDs = this.selectedClasses.join(",");
        this.invObj.multiCategoryIDs = this.selectedCategories.join(",");
        this.invObj.totalValue = Number(this.invObj.totalValue) || 0;
        if (this.isInventoryIuss) {
          this.invObj.brand = this.invObj.brand || "";
          this.invObj.warranty = this.invObj.warranty || "";
          this.invObj.specification = this.invObj.specification || "";
          if (!this.invObj.purchaseDate) {
            this.invObj.purchaseDate = null;
          }
        }

        var status = await this.post({
          url:
            this.$store.state.domain +
            "posStock/saveStock?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: this.invObj,
          message: "Stock added successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.LoadData(this.current);
          this.visibility1 = false;
        }
        this.request = false;

        // if (this.invObj.id == 0) {
        //   var status = await this.post({
        //     url:
        //       this.$store.state.domain +
        //       "posStock?db=" +
        //       this.$store.state.userData.db,
        //     body: this.invObj,
        //     message: "Stock added successfully!",
        //     context: this,
        //     token: this.$store.state.userData.token,
        //   });
        // } else {
        //   var status = await this.put({
        //     url:
        //       this.$store.state.domain +
        //       "posStock/" +
        //       this.invObj.id +
        //       "?db=" +
        //       this.$store.state.userData.db,
        //     message: "Stock updated successfully!",
        //     context: this,
        //     body: this.invObj,
        //     token: this.$store.state.userData.token,
        //   });
        // }
      }
    },
    async delProduct(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "posStock/" +
            id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Stock removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData(this.current);
      }
    },
    openHold(item) {
      this.holdObj = { ...item };
      this.$bvModal.show("modal-hold");
    },
    CheckDate() {
      var elem = this.$refs["holdDate"];
      if (!this.holdObj.holdTill) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async markHold() {
      if (this.CheckDate() == true) {
        this.holding = true;
        this.holdObj.isHold = true;
        var status = await this.put({
          url:
            this.$store.state.domain +
            "posStock/" +
            this.holdObj.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Stock hold successfully!",
          context: this,
          body: this.holdObj,
          token: this.$store.state.userData.token,
        });
        if (status) {
          this.$bvModal.hide("modal-hold");
          this.LoadData(this.current);
        }
        this.holding = false;
      }
    },
    async unhold(item) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: "Do you want to unhold the stock!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, Unhold it!",
      });

      if (result.isConfirmed) {
        item.isHold = false;
        var status = await this.put({
          url:
            this.$store.state.domain +
            "posStock/" +
            item.id +
            "?db=" +
            this.$store.state.userData.db,
          message: "Stock unhold successfully!",
          context: this,
          body: item,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData(this.current);
      }
    },

    async LoadCat(val) {
      if (!this.isInventoryIuss) {
        var obj = {
          url:
            this.$store.state.domain +
            "posCategories?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        let res = await this.get(obj);
        this.categories = Array.isArray(res)
          ? res
          : Array.isArray(res && res.data)
          ? res.data
          : [];
        this.iussParentCategories = [];
        this.iussSubCategories = [];
      } else {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "posCategories/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          returnData: true,
        });
        const parsed = this.parseIussCategoryLoad(res);
        this.iussParentCategories = parsed.categories;
        this.iussSubCategories = parsed.subCategories;
        this.categories = parsed.all;
      }
      if (
        (this.categories.length > 0 && val == 0) ||
        this.categories.length == 1
      ) {
        const first =
          this.isInventoryIuss && this.iussParentCategories.length
            ? this.iussParentCategories[0]
            : this.categories[0];
        this.LoadData(first.id);
      }
    },

    parseIussCategoryLoad(res) {
      const empty = { categories: [], subCategories: [], all: [] };
      if (!res) return empty;

      // Full API body: { status, data: { data, categories, subCategories } }
      // or { status, data: [...], categories, subCategories }
      // or already unwrapped { data, categories, subCategories }
      let bundle = res;
      if (
        res.data &&
        !Array.isArray(res.data) &&
        (Array.isArray(res.data.data) ||
          Array.isArray(res.data.categories) ||
          Array.isArray(res.data.subCategories))
      ) {
        bundle = res.data;
      }

      const categories = Array.isArray(bundle.categories)
        ? bundle.categories
        : Array.isArray(res.categories)
        ? res.categories
        : [];
      const subCategories = Array.isArray(bundle.subCategories)
        ? bundle.subCategories
        : Array.isArray(res.subCategories)
        ? res.subCategories
        : [];
      let all = [];
      if (Array.isArray(bundle.data) && bundle.data.length) {
        all = bundle.data;
      } else if (
        Array.isArray(res.data) &&
        res.data.length &&
        !categories.length &&
        !subCategories.length
      ) {
        all = res.data;
      } else {
        all = categories.concat(subCategories);
      }

      return { categories, subCategories, all: all || [] };
    },
    async LoadWare() {
      var obj = {
        url:
          this.$store.state.domain +
          "posWarehouse?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.warehouses = await this.get(obj);
    },
    async LoadSupplier() {
      var obj = {
        url:
          this.$store.state.domain +
          "posSupplier?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.suppliers = await this.get(obj);
    },
    async LoadUsers() {
      var obj = {
        url:
          this.$store.state.domain +
          "Users/LoadUsers?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.allUsers = await this.get(obj);
      // console.log(this.allUsers);
    },
    async LoadClasses() {
      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      let res = await this.get(obj);
      this.classesOptions = res.map((c) => c.cls);
    },
    async LoadAccounts() {
      this.accLoading = true;
      var obj = {
        url:
          this.$store.state.domain +
          "Accounts/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.accList = await this.get(obj);
      // console.log("acc", this.accList);
      this.accLoading = false;
    },

    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [
          "manage-bundles",
          "inventory_account",
          "inventory_settings",
          "inventory_iuss",
          "inventory_low_stock_level",
        ],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      // console.log(res);
      this.bundleObj = res.find((el) => el.key == "manage-bundles");
      if (this.bundleObj?.valueBool) {
        this.LoadAllProducts();
      }
      this.accObj = res.find((el) => el.key == "inventory_account");
      if (this.accObj?.valueBool) {
        this.LoadAccounts();
      }
      this.invSetting = res.find((el) => el.key == "inventory_settings");
      this.inventoryIussSetting = res.find((el) => el.key == "inventory_iuss");
      this.lowStockSetting = res.find(
        (el) => el.key == "inventory_low_stock_level"
      );
      if (!this.lowStockSetting) {
        this.lowStockSetting = {
          id: 0,
          key: "inventory_low_stock_level",
          value: "",
          valueBool: true,
          valueNumber: 0,
          campusID: this.$store.state.userData.cId,
        };
      }
      if (this.isInventoryIuss) {
        await this.LoadCat(0);
        this.LoadIussImportOptions();
      }
    },

    async LoadIussImportOptions() {
      if (!this.isInventoryIuss) {
        this.iussImportOptions = null;
        return;
      }
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "posCategories/GetImportOptions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.iussImportOptions = res || null;
      } catch (e) {
        this.iussImportOptions = null;
      }
    },

    iussCategoryLabel(d) {
      if (!d) return "";
      if (!this.isInventoryIuss) return d.name;
      return this.formatCategoryOptionLabel(d);
    },

    isIussParentCategory(c) {
      if (!c) return true;
      if (c.type) {
        const t = String(c.type).toLowerCase();
        if (t === "category" || t === "parent") return true;
        if (t === "sub" || t === "subcategory" || t === "sub-category")
          return false;
      }
      return !c.parentCategoryID || Number(c.parentCategoryID) === 0;
    },

    iussCategoryTypeLabel(c) {
      if (c && c.type) return c.type;
      return this.isIussParentCategory(c) ? "Category" : "Sub";
    },

    formatCategoryOptionLabel(c) {
      if (!c) return "";
      if (!this.isInventoryIuss) return c.name || "";
      const code = c.fullCode || c.code || "";
      const isSub = !this.isIussParentCategory(c);
      if (isSub) {
        const parentName =
          c.meta_parentCategoryName ||
          (
            (this.iussParentCategories || []).find(
              (p) => Number(p.id) === Number(c.parentCategoryID)
            ) ||
            (this.categories || []).find(
              (p) => Number(p.id) === Number(c.parentCategoryID)
            ) ||
            {}
          ).name ||
          "";
        const base = parentName
          ? parentName + " › " + (c.name || "")
          : "↳ " + (c.name || "");
        return code ? base + " (" + code + ")" : base;
      }
      return code ? (c.name || "") + " (" + code + ")" : c.name || "";
    },

    readSpreadsheetRows(file) {
      return new Promise((resolve, reject) => {
        const name = (file.name || "").toLowerCase();
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.onload = (e) => {
          try {
            if (name.endsWith(".json")) {
              const parsed = JSON.parse(String(e.target.result || "[]"));
              resolve(Array.isArray(parsed) ? parsed : []);
              return;
            }
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const rawRows = XLSX.utils.sheet_to_json(worksheet, {
              defval: "",
              raw: false,
            });
            resolve(rawRows || []);
          } catch (err) {
            reject(err);
          }
        };
        if (name.endsWith(".json")) {
          reader.readAsText(file);
        } else {
          reader.readAsArrayBuffer(file);
        }
      });
    },

    mapIussImportRow(row) {
      return {
        brCode: this.stockImportCell(row, "brCode", "br code", "br. code"),
        categoryCode: this.stockImportCell(
          row,
          "categoryCode",
          "category code"
        ),
        categoryName: this.stockImportCell(
          row,
          "categoryName",
          "category name"
        ),
        subCategoryName: this.stockImportCell(
          row,
          "subCategoryName",
          "sub category name",
          "subcategory name"
        ),
        subCategoryCode: this.stockImportCell(
          row,
          "subCategoryCode",
          "sub category code",
          "subcategory code",
          "sub code"
        ),
        serialNo: this.stockImportCell(row, "serialNo", "serial no", "serial"),
        parentAccount: this.stockImportCell(
          row,
          "parentAccount",
          "parent account"
        ),
        expenseAccount: this.stockImportCell(
          row,
          "expenseAccount",
          "expense account"
        ),
      };
    },

    downloadIussCategorySample() {
      const sample = [
        {
          brCode: "036",
          categoryCode: "001",
          categoryName: "Furniture & Fixtures",
          subCategoryName: "EY Tables (Plastic)",
          subCategoryCode: "0001",
          serialNo: "00001",
          parentAccount: "",
          expenseAccount: "",
        },
        {
          brCode: "036",
          categoryCode: "001",
          categoryName: "Furniture & Fixtures",
          subCategoryName: "EY Tables (Wooden)",
          subCategoryCode: "0002",
          serialNo: "00001",
          parentAccount: "",
          expenseAccount: "",
        },
        {
          brCode: "036",
          categoryCode: "001",
          categoryName: "Furniture & Fixtures",
          subCategoryName: "Dual Desk Table",
          subCategoryCode: "0003",
          serialNo: "00001",
          parentAccount: "",
          expenseAccount: "",
        },
        {
          brCode: "036",
          categoryCode: "002",
          categoryName: "Electronics/Electric Items",
          subCategoryName: "A.C Inner Unit 01 Ton",
          subCategoryCode: "0001",
          serialNo: "00001",
          parentAccount: "",
          expenseAccount: "",
        },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(sample),
        "SubCategories"
      );
      XLSX.writeFile(wb, "iuss-subcategory-import-sample.xlsx");
    },

    async handleIussCategoryImportFile(event) {
      const file = event.target.files && event.target.files[0];
      if (event.target) event.target.value = "";
      if (!file || !this.isInventoryIuss) return;
      this.iussImportLoading = true;
      try {
        const rawRows = await this.readSpreadsheetRows(file);
        const body = rawRows
          .map((r) => this.mapIussImportRow(r))
          .filter(
            (r) =>
              r.subCategoryName ||
              r.subCategoryCode ||
              r.categoryName ||
              r.categoryCode
          );
        if (!body.length) {
          this.$bvToast.toast(this.$t("No valid rows found in file"), {
            title: this.$t("Import"),
            variant: "warning",
            solid: true,
          });
          return;
        }
        const status = await this.post({
          url:
            this.$store.state.domain +
            "posCategories/ImportSubCategories?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body,
          message: "Categories imported successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) {
          await this.LoadCat(0);
          this.LoadIussImportOptions();
        }
      } catch (err) {
        console.error(err);
        this.$bvToast.toast(this.$t("Failed to import categories"), {
          title: this.$t("Import"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.iussImportLoading = false;
      }
    },

    async LoadAllProducts() {
      var obj = {
        url:
          this.$store.state.domain +
          "posStock/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.allProducts = await this.get(obj);
    },

    async openBundle(product) {
      this.selectedProduct = product;
      this.bundleDataLoading = true;

      const obj = {
        url:
          this.$store.state.domain +
          "posStock/getBundleData?db=" +
          this.$store.state.userData.db +
          "&stockID=" +
          product.id,
        token: this.$store.state.userData.token,
      };

      const bundleData = await this.get(obj);

      if (bundleData && bundleData.length > 0) {
        this.bundleItems = bundleData;
      } else {
        this.bundleItems = [
          {
            id: 0,
            stockBundleID: product.id,
            stockID: 0,
            qty: 1,
            campusID: this.$store.state.userData.cId,
          },
        ];
      }
      this.bundleDataLoading = false;
      this.$bvModal.show("modal-bundle");
    },

    closeBundle() {
      this.selectedProduct = null;
      this.bundleItems = [];
      this.$bvModal.hide("modal-bundle");
    },

    addBundleItem() {
      this.bundleItems.push({
        id: 0,
        stockBundleID: this.selectedProduct.id,
        stockID: 0,
        qty: 1,
        campusID: this.$store.state.userData.cId,
      });
    },

    removeBundleItem(index) {
      this.bundleItems.splice(index, 1);
    },

    isValidBundle() {
      // First check if there are any items
      if (this.bundleItems.length === 0) {
        return false;
      }

      // First validate
      const isValid = this.bundleItems.every(
        (item) => item.stockID > 0 && parseInt(item.qty) > 0
      );

      // Then modify if valid
      if (isValid) {
        this.bundleItems.forEach((item) => {
          item.qty = parseInt(item.qty);
        });
      }

      return isValid;
    },

    async saveBundle() {
      if (!this.isValidBundle()) {
        this.$bvToast.toast("Please fill all bundle items correctly!", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-right",
        });
        return;
      }

      this.bundleSaving = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "posStock/saveBundle?db=" +
            this.$store.state.userData.db +
            "&stockID=" +
            this.selectedProduct.id,
          body: this.bundleItems,
          token: this.$store.state.userData.token,
          message: "Bundle created successfully!",
          context: this,
        };

        const status = await this.post(obj);
        if (status) {
          // this.LoadData(this.current);
          this.closeBundle();
        }
      } catch (error) {
        console.error("Error creating bundle:", error);
      }
      this.bundleSaving = false;
    },

    openStockImport() {
      this.importSidebarVisible = true;
      this.importResultSummary = null;
      this.importCreatedItems = [];
      if (!this.importWarehouseID && this.warehouses.length) {
        this.importWarehouseID = this.warehouses[0].id;
      }
    },

    downloadStockImportSample() {
      if (this.isInventoryIuss) {
        const sample = [
          {
            name: "EY Tables (Plastic)",
            category: "Furniture",
            vendor: "None",
            warehouse: "Main Store",
            sku: "SKU-001",
            quantity: 10,
            sellingPrice: 0,
            purchasePrice: 5000,
            restockQuantity: 2,
            brand: "ABC",
            warranty: "1 Year",
            purchaseDate: "2024-01-15",
            specification: "Plastic tables for early years",
          },
        ];
        const ws = XLSX.utils.json_to_sheet(sample);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock");
        XLSX.writeFile(wb, "pos-stock-import-sample-iuss.xlsx");
        return;
      }
      const link = document.createElement("a");
      link.href = "/pos-stock-import-sample.xlsx";
      link.download = "pos-stock-import-sample.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    clearStockImport() {
      this.stockImportRows = [];
      this.stockImportValidationErrors = [];
      this.importResultSummary = null;
      this.importCreatedItems = [];
      if (this.$refs.stockImportFile) {
        this.$refs.stockImportFile.value = "";
      }
    },

    stockImportCreatedKindLabel(kind) {
      if (kind === "category") return this.$t("Category");
      if (kind === "supplier") return this.$t("Vendor");
      if (kind === "warehouse") return this.$t("Warehouse");
      return kind;
    },

    stockImportCell(row, ...keys) {
      for (const key of keys) {
        const found = Object.keys(row).find(
          (k) => k.trim().toLowerCase() === key.trim().toLowerCase()
        );
        if (found !== undefined && row[found] !== null && row[found] !== "") {
          return String(row[found]).trim();
        }
      }
      return "";
    },

    stockImportNumber(val, fallback = 0) {
      if (val === null || val === undefined || val === "") return fallback;
      const cleaned =
        typeof val === "string" ? val.replace(/,/g, "").trim() : val;
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : fallback;
    },

    stockImportDate(val) {
      if (val === null || val === undefined || val === "") return null;
      const s = String(val).trim();
      if (!s) return null;
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return d.toISOString();
      return s;
    },

    mapStockImportRow(row, excelRowNumber) {
      const name = this.stockImportCell(row, "name", "product", "product name");
      const category = this.stockImportCell(row, "category");
      const vendor = this.stockImportCell(row, "vendor", "supplier");
      const warehouse = this.stockImportCell(row, "warehouse", "location");
      const sku = this.stockImportCell(row, "sku", "sku number");

      const quantity = this.stockImportNumber(
        this.stockImportCell(row, "quantity", "qty")
      );
      const sellingPrice = this.stockImportNumber(
        this.stockImportCell(row, "sellingprice", "selling price", "price")
      );
      const purchasePrice = this.stockImportNumber(
        this.stockImportCell(row, "purchaseprice", "purchase price")
      );
      const restockQuantity = this.stockImportNumber(
        this.stockImportCell(row, "restockquantity", "restock quantity")
      );

      const mapped = {
        _excelRow: excelRowNumber,
        name,
        category,
        vendor: vendor || "None",
        warehouse,
        sku,
        quantity,
        sellingPrice,
        purchasePrice,
        restockQuantity,
      };

      if (this.isInventoryIuss) {
        mapped.brand = this.stockImportCell(row, "brand");
        mapped.warranty = this.stockImportCell(
          row,
          "warranty",
          "warantty",
          "warrenty"
        );
        mapped.purchaseDate = this.stockImportDate(
          this.stockImportCell(row, "purchaseDate", "purchase date", "purchasedate")
        );
        mapped.specification = this.stockImportCell(
          row,
          "specification",
          "spacification",
          "specifications",
          "spec"
        );
      }

      return mapped;
    },

    validateStockImportRows(rows) {
      const errors = [];
      rows.forEach((row) => {
        if (!row.name) {
          errors.push({
            row: row._excelRow,
            message: this.$t("Product name is required"),
          });
        }
        if (!row.category) {
          errors.push({
            row: row._excelRow,
            message: this.$t("Category is required"),
          });
        }
        if (!row.warehouse && !this.importWarehouseID) {
          errors.push({
            row: row._excelRow,
            message: this.$t(
              "Warehouse is required in the file or select a default warehouse"
            ),
          });
        }
      });
      return errors;
    },

    handleStockImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rawRows = XLSX.utils.sheet_to_json(worksheet, {
            defval: "",
            raw: false,
          });

          const parsed = rawRows
            .map((row, idx) => this.mapStockImportRow(row, idx + 2))
            .filter(
              (row) =>
                row.name ||
                row.category ||
                row.sku ||
                row.quantity ||
                row.sellingPrice
            );

          this.stockImportRows = parsed;
          this.importResultSummary = null;
          this.importCreatedItems = [];
          this.stockImportValidationErrors =
            this.validateStockImportRows(parsed);

          if (!parsed.length) {
            this.$bvToast.toast(this.$t("No data rows found in the file."), {
              title: this.$t("Error!"),
              variant: "warning",
              toaster: "b-toaster-top-center",
            });
          }
        } catch (err) {
          console.error("handleStockImportFile", err);
          this.$bvToast.toast(this.$t("Could not read the Excel file."), {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        }
      };
      reader.readAsArrayBuffer(file);
    },

    mergeStockImportCreated(target, source, key) {
      const list = source?.[key] || [];
      list.forEach((item) => {
        if (!item?.id) return;
        if (!target[key].some((x) => x.id === item.id)) {
          target[key].push(item);
        }
      });
    },

    buildStockImportCreatedItems(created) {
      const items = [];
      (created.categories || []).forEach((c) => {
        if (c?.name) items.push({ kind: "category", name: c.name });
      });
      (created.suppliers || []).forEach((s) => {
        if (s?.name) items.push({ kind: "supplier", name: s.name });
      });
      (created.warehouses || []).forEach((w) => {
        if (w?.name) items.push({ kind: "warehouse", name: w.name });
      });
      return items;
    },

    buildStockImportUrl() {
      const { db, cId, userID } = this.$store.state.userData;
      let url =
        `${this.$store.state.domain}posStock/Import?db=${db}` +
        `&cID=${cId}&userID=${userID}`;
      if (this.importWarehouseID) {
        url += `&warehouseID=${this.importWarehouseID}`;
      }
      return url;
    },

    stockImportPayloadRow(row) {
      const payload = {
        name: row.name,
        category: row.category,
        vendor: row.vendor,
        warehouse: row.warehouse,
        sku: row.sku,
        quantity: row.quantity,
        sellingPrice: this.hideSellingPrice ? 0 : row.sellingPrice,
        purchasePrice: this.hideSellingPrice ? 0 : row.purchasePrice,
        restockQuantity: row.restockQuantity,
      };
      if (this.isInventoryIuss) {
        payload.brand = row.brand || "";
        payload.warranty = row.warranty || "";
        payload.purchaseDate = row.purchaseDate || null;
        payload.specification = row.specification || "";
      }
      return payload;
    },

    async runStockImport() {
      if (!this.stockImportRows.length) return;

      this.stockImportValidationErrors = this.validateStockImportRows(
        this.stockImportRows
      );
      if (this.stockImportValidationErrors.length) return;

      this.importLoading = true;
      this.importResultSummary = null;
      this.importCreatedItems = [];

      const batchSize = this.stockImportBatchSize;
      const totalBatches = Math.ceil(this.stockImportRows.length / batchSize);
      this.importBatchTotal = totalBatches;
      this.importBatchCurrent = 0;

      const aggregate = {
        imported: [],
        skipped: [],
        created: { categories: [], suppliers: [], warehouses: [] },
        summary: {
          importedCount: 0,
          skippedCount: 0,
          createdCategoriesCount: 0,
          createdSuppliersCount: 0,
          createdWarehousesCount: 0,
        },
      };

      try {
        for (let i = 0; i < this.stockImportRows.length; i += batchSize) {
          const chunk = this.stockImportRows
            .slice(i, i + batchSize)
            .map((row) => this.stockImportPayloadRow(row));

          const response = await this.post({
            url: this.buildStockImportUrl(),
            body: chunk,
            token: this.$store.state.userData.token,
            context: this,
            isBulkUpload: true,
          });

          if (!response || response.status !== "success") {
            const msg =
              response?.message ||
              response?.data ||
              this.$t("Import failed on batch") +
                ` ${this.importBatchCurrent + 1}`;
            throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
          }

          const data = response.data || {};
          aggregate.imported.push(...(data.imported || []));
          aggregate.skipped.push(...(data.skipped || []));
          if (data.created) {
            this.mergeStockImportCreated(
              aggregate.created,
              data.created,
              "categories"
            );
            this.mergeStockImportCreated(
              aggregate.created,
              data.created,
              "suppliers"
            );
            this.mergeStockImportCreated(
              aggregate.created,
              data.created,
              "warehouses"
            );
          }

          if (data.summary) {
            aggregate.summary.importedCount += data.summary.importedCount || 0;
            aggregate.summary.skippedCount += data.summary.skippedCount || 0;
            aggregate.summary.createdCategoriesCount +=
              data.summary.createdCategoriesCount || 0;
            aggregate.summary.createdSuppliersCount +=
              data.summary.createdSuppliersCount || 0;
            aggregate.summary.createdWarehousesCount +=
              data.summary.createdWarehousesCount || 0;
          }

          this.importBatchCurrent += 1;
        }

        this.importResultSummary = {
          importedCount: aggregate.summary.importedCount || aggregate.imported.length,
          skippedCount: aggregate.summary.skippedCount || aggregate.skipped.length,
        };
        this.importCreatedItems = this.buildStockImportCreatedItems(
          aggregate.created
        );

        this.$bvToast.toast(
          `${this.$t("Imported")} ${this.importResultSummary.importedCount} ${this.$t(
            "records"
          )}`,
          {
            title: this.$t("Success"),
            variant: "success",
            toaster: "b-toaster-top-center",
          }
        );

        this.stockImportRows = [];
        this.stockImportValidationErrors = [];
        if (this.$refs.stockImportFile) {
          this.$refs.stockImportFile.value = "";
        }
        this.LoadData(this.current);
        this.LoadCat(0);
        if (this.bundleObj?.valueBool) {
          this.LoadAllProducts();
        }
      } catch (error) {
        console.error("runStockImport", error);
        this.$bvToast.toast(
          error.message || this.$t("Import failed. Please try again."),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      }

      this.importLoading = false;
      this.importBatchCurrent = 0;
      this.importBatchTotal = 0;
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style scoped>
.inv-photo-upload {
  display: flex;
  justify-content: center;
  width: 100%;
}

.inv-photo-upload__slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  min-width: 120px;
}

.inv-photo-upload__preview {
  position: relative;
  display: flex;
  align-items: center;
}

.inv-photo-upload__img {
  border-radius: 10px;
  height: 120px;
  width: 120px;
  object-fit: cover;
  cursor: pointer;
  display: block;
}

.inv-photo-upload__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 10px;
  z-index: 2;
}

.inv-photo-upload__delete {
  margin-left: 0.5rem;
}

.inv-toolbar {
  gap: 0;
}

.inv-toolbar__search {
  flex: 1 1 12rem;
  min-width: 0;
}

.inv-toolbar__btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.inv-toolbar__action {
  flex-shrink: 0;
  margin-left: 0.5rem;
}

@media (min-width: 992px) {
  .inv-toolbar__search {
    flex: 1 1 auto;
  }
}

.stock-import-result__alert {
  padding: 0.75rem 1rem !important;
}

.stock-import-result__title {
  font-weight: 600;
  font-size: 1rem;
  color: #1e5631;
}

.stock-import-result__stats {
  font-size: 0.9rem;
  color: #2d4a36;
  margin-top: 0.25rem;
}

.stock-import-created {
  background: #f3f2f7;
  border: 1px solid #dcdce4;
  border-radius: 0.428rem;
  padding: 0.75rem 1rem;
}

.stock-import-created__heading {
  font-weight: 600;
  font-size: 0.95rem;
  color: #3b4253;
  margin-bottom: 0.5rem;
}

.stock-import-created__list {
  list-style: none;
  padding-left: 0;
}

.stock-import-created__list li {
  padding: 0.35rem 0;
  font-size: 0.925rem;
  line-height: 1.4;
  border-bottom: 1px solid #e4e4e8;
}

.stock-import-created__list li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.stock-import-created__kind {
  font-weight: 600;
  color: #3b4253;
}

.stock-import-created__name {
  color: #22292f;
  font-weight: 500;
}
</style>
